import { useEffect } from 'react';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl, FormCurrencyField, InputAdornment } from 'components/common';
import { IBonusAndCommision } from './types';
import { ApplicationFormPageEnum } from '../types';
import { bonusAndCommisionQuestions } from './questions';
import { getCurrentRoute } from 'utils/location';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';

export const BonusAndCommision = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const hasSecondJobParam = searchParams.get('hasSecondJob');
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const form = useForm<IBonusAndCommision>({
    defaultValues: {
      commission: '',
      hasCommission: null,
      overtime: '',
      hasOvertime: null,
      bonus: '',
      hasBonus: null,
    },
  });
  const formFields = form.watch();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { commission, overtime, bonus } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        commission,
        hasCommission: commission ? 'true' : 'false',
        overtime,
        hasOvertime: overtime ? 'true' : 'false',
        bonus,
        hasBonus: bonus ? 'true' : 'false',
      });
    }
  }, [applicationInProgress]);

  useEffect(() => {
    if (!formFields.hasCommission && formFields.commission) return form.setValue('commission', '');
    if (!formFields.hasOvertime && formFields.overtime) return form.setValue('overtime', '');
    if (!formFields.hasBonus && formFields.bonus) return form.setValue('bonus', '');
  }, [form, formFields]);

  const submitFormHandler = async (formData: IBonusAndCommision) => {
    const { commission, overtime, bonus } = formData;

    const questions = Object.entries({ commission, overtime, bonus }).map(([key, answer]) => ({
      question: intl.formatMessage({ id: bonusAndCommisionQuestions[key].labelKey }),
      key,
      answer: answer || 0,
    }));

    patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    const nextPage = hasSecondJobParam ? ApplicationFormPageEnum.OTHER_INCOME : ApplicationFormPageEnum.SECOND_JOB;
    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        {Object.entries(bonusAndCommisionQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}

            {!formFields[question.booleanQuestionFormKey as keyof IBonusAndCommision] ||
            formFields[question.booleanQuestionFormKey as keyof IBonusAndCommision] === 'false' ? (
              <FormControl
                name={question.booleanQuestionFormKey}
                radioOptions={question.options}
                radioGroupProps={{
                  className: 'mt-2',
                }}
                onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setValue(question.booleanQuestionFormKey, event.target.value);
                }}
              />
            ) : (
              <FormCurrencyField
                name={key}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <FormattedMessage id={question.adornmentKey} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
